import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getCustomers, getProducts, createTailoringOrder, createCustomer, updateCustomerProfile } from '../lib/api';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (n) => Number(n || 0).toLocaleString('es-DO', { style: 'currency', currency: 'DOP' });

const isService = (product) => {
  const type = String(product?.product_type || product?.type || '').trim().toUpperCase();
  return type === 'SERVICIO' || type === 'SERVICE' || product?.track_inventory === false || product?.track_inventory === 'false' || product?.track_inventory === 0;
};

const defaultDueDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

const TailoringOrderNew = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [serviceQty, setServiceQty] = useState('1');

  const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2', '4', '6', '8', '10', '12', '14', '16'];

  const [form, setForm] = useState({
    customer_id: '',
    product_id: '',
    total_amount: '',
    delivery_due_date: defaultDueDate(),
    services: [],
    measurements: { pecho: '', cintura: '', cadera: '', largo: '', manga: '' },
    size: '',
    color: '',
    changes_requested: '',
    notes: '',
  });

  const baseProducts = useMemo(() => products.filter((p) => !isService(p)), [products]);
  const serviceProducts = useMemo(() => products.filter((p) => isService(p)), [products]);

  const servicesTotal = useMemo(() => {
    return (form.services || []).reduce((sum, s) => sum + Number(s.subtotal || 0), 0);
  }, [form.services]);

  const load = async () => {
    try {
      const [cust, prods] = await Promise.all([
        getCustomers(),
        getProducts({}),
      ]);
      setCustomers(cust || []);
      setProducts(prods || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error cargando datos');
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setMeas = (k, v) => setForm((p) => ({ ...p, measurements: { ...p.measurements, [k]: v } }));

  const [newCustModal, setNewCustModal] = useState(false);
  const [profileModal, setProfileModal] = useState(null);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [newCust, setNewCust] = useState({ name: '', last_name: '', phone: '' });
  const [savingCust, setSavingCust] = useState(false);

  const handleCreateCustomer = async () => {
    if (!newCust.name.trim() || !newCust.last_name.trim() || !newCust.phone.trim()) {
      toast.error('Nombre, apellido y teléfono son requeridos');
      return;
    }
    setSavingCust(true);
    try {
      const created = await createCustomer(newCust);
      setCustomers((prev) => [...prev, created]);
      setField('customer_id', created.id);
      setNewCustModal(false);
      setNewCust({ name: '', last_name: '', phone: '' });
      toast.success(`Cliente ${created.name} ${created.last_name} creado`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error creando cliente');
    } finally {
      setSavingCust(false);
    }
  };

  // Estado de checks y notas por medida
  const MEAS_KEYS = ['pecho', 'cintura', 'cadera', 'largo', 'manga'];
  const [measEnabled, setMeasEnabled] = useState(() => Object.fromEntries(MEAS_KEYS.map((k) => [k, false])));
  const [measNotes, setMeasNotes] = useState(() => Object.fromEntries(MEAS_KEYS.map((k) => [k, ''])));

  const toggleMeas = (k) => {
    setMeasEnabled((p) => {
      const next = { ...p, [k]: !p[k] };
      // Si se desactiva, limpiar valor y nota
      if (!next[k]) {
        setMeas(k, '');
        setMeasNotes((n) => ({ ...n, [k]: '' }));
      }
      return next;
    });
  };

  const handleProductChange = (productId) => {
    const product = baseProducts.find((p) => p.id === productId);
    setForm((p) => ({
      ...p,
      product_id: productId,
      total_amount: p.total_amount || String(Number(product?.price || 0) + servicesTotal),
    }));
  };

  const addService = () => {
    const service = serviceProducts.find((p) => p.id === selectedServiceId);
    if (!service) return toast.error('Selecciona un servicio');

    const qty = toNumber(serviceQty || 1);
    if (qty <= 0) return toast.error('La cantidad del servicio debe ser mayor que 0');

    const unitPrice = Number(service.price || 0);
    const subtotal = qty * unitPrice;

    setForm((p) => {
      const existing = (p.services || []).find((s) => s.product_id === service.id);
      let nextServices;
      if (existing) {
        nextServices = (p.services || []).map((s) => {
          if (s.product_id !== service.id) return s;
          const newQty = Number(s.quantity || 0) + qty;
          return { ...s, quantity: newQty, subtotal: newQty * Number(s.unit_price || 0) };
        });
      } else {
        nextServices = [
          ...(p.services || []),
          {
            product_id: service.id,
            name: service.name,
            quantity: qty,
            unit_price: unitPrice,
            subtotal,
            customization: '',
          },
        ];
      }

      const currentTotal = toNumber(p.total_amount);
      return {
        ...p,
        services: nextServices,
        total_amount: String(currentTotal + subtotal),
      };
    });

    setSelectedServiceId('');
    setServiceQty('1');
  };

  const updateServiceCustomization = (productId, value) => {
    setForm((p) => ({
      ...p,
      services: (p.services || []).map((s) =>
        s.product_id === productId ? { ...s, customization: value } : s
      ),
    }));
  };

  const removeService = (productId) => {
    setForm((p) => {
      const service = (p.services || []).find((s) => s.product_id === productId);
      const subtotal = Number(service?.subtotal || 0);
      const nextTotal = Math.max(0, toNumber(p.total_amount) - subtotal);
      return {
        ...p,
        services: (p.services || []).filter((s) => s.product_id !== productId),
        total_amount: String(nextTotal || ''),
      };
    });
  };

  const submit = async () => {
    try {
      setSaving(true);

      const payload = {
        customer_id: form.customer_id,
        product_id: form.product_id,
        total_amount: toNumber(form.total_amount),
        delivery_due_date: form.delivery_due_date,
        services: (form.services || []).map((s) => ({
          product_id: s.product_id,
          quantity: Number(s.quantity || 1),
          unit_price: Number(s.unit_price || 0),
          customization: s.customization || '',
        })),
        measurements: Object.fromEntries(
          MEAS_KEYS
            .filter((k) => measEnabled[k])
            .map((k) => [k, `${form.measurements[k]}${measNotes[k] ? ` — ${measNotes[k]}` : ''}`])
        ),
        changes_requested: form.changes_requested,
        size: form.size,
        color: form.color,
        notes: form.notes,
      };

      if (!payload.customer_id) return toast.error('Selecciona un cliente');
      if (!payload.product_id) return toast.error('Selecciona un producto base');
      if (payload.total_amount <= 0) return toast.error('Total inválido');
      const created = await createTailoringOrder(payload);
      toast.success('Pedido creado');

      // Detectar si hay cambios en el perfil del cliente
      if (form.customer_id) {
        const cust = customers.find((c) => c.id === form.customer_id);
        if (cust) {
          const activeMeas = Object.fromEntries(
            MEAS_KEYS.filter((k) => measEnabled[k]).map((k) => [k, form.measurements[k]])
          );
          const profileChanges = [];
          if (form.size && form.size !== (cust.size || ''))
            profileChanges.push({ label: 'Talla', from: cust.size || '—', to: form.size });
          if (form.color && form.color !== (cust.preferred_color || ''))
            profileChanges.push({ label: 'Color', from: cust.preferred_color || '—', to: form.color });
          if (form.changes_requested && form.changes_requested !== (cust.modifications || ''))
            profileChanges.push({ label: 'Modificaciones', from: cust.modifications || '—', to: form.changes_requested });
          Object.entries(activeMeas).forEach(([k, v]) => {
            const oldVal = (cust.measurements || {})[k] || '';
            if (v && v !== oldVal)
              profileChanges.push({ label: `Medida ${k}`, from: oldVal || '—', to: v });
          });

          if (profileChanges.length > 0) {
            setProfileModal({
              customerId: form.customer_id,
              customerName: `${cust.name} ${cust.last_name}`,
              orderId: created.id,
              orderCode: created.code || '',
              changes: profileChanges,
              payload: {
                order_code: created.code || '',
                size: form.size || '',
                preferred_color: form.color || '',
                modifications: form.changes_requested || '',
                measurements: activeMeas,
              },
            });
            return;
          }
        }
      }
      navigate(`/pos?tailoring_order_id=${created.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error creando pedido');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileUpdate = async (doUpdate) => {
    if (doUpdate) {
      setUpdatingProfile(true);
      try {
        await updateCustomerProfile(profileModal.customerId, profileModal.payload);
        toast.success('Perfil del cliente actualizado');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Error actualizando perfil');
      } finally {
        setUpdatingProfile(false);
      }
    }
    const orderId = profileModal?.orderId;
    setProfileModal(null);
    navigate(`/pos?tailoring_order_id=${orderId}`);
  };

  return (
    <div className="p-4 space-y-6" data-testid="tailoring-new-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Nuevo Pedido de Confección</h1>
        <p className="text-slate-600">El pedido queda en espera de producción. Sucursal y vendedor se toman del usuario logueado.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Datos principales</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>Cliente</Label>
              <button
                type="button"
                onClick={() => setNewCustModal(true)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                <UserPlus className="w-3 h-3" />
                Nuevo cliente
              </button>
            </div>
            <Select
              value={form.customer_id}
              onValueChange={(v) => {
                const cust = customers.find((c) => c.id === v);
                setForm((p) => ({
                  ...p,
                  customer_id: v,
                  size: cust?.size || p.size,
                  color: cust?.preferred_color || p.color,
                  changes_requested: cust?.modifications || p.changes_requested,
                }));
              }}
            >
              <SelectTrigger><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} {c.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Producto base (referencia)</Label>
            <Select value={form.product_id} onValueChange={handleProductChange}>
              <SelectTrigger><SelectValue placeholder="Selecciona producto" /></SelectTrigger>
              <SelectContent>
                {baseProducts.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Fecha entrega</Label>
            <Input type="date" value={form.delivery_due_date} onChange={(e) => setField('delivery_due_date', e.target.value)} />
            <div className="text-xs text-slate-500">Si no se cambia, queda a 7 días.</div>
          </div>

          <div className="space-y-1">
            <Label>Total del pedido (DOP)</Label>
            <Input value={form.total_amount} onChange={(e) => setField('total_amount', e.target.value)} placeholder="3500" />
          </div>

          <div className="md:col-span-2 text-sm text-slate-600 flex items-end">
            Los cobros o abonos se facturan desde el detalle del pedido después de crearlo.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Medidas y Modificaciones</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Size, Color */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Talla</Label>
              <Select value={form.size || ''} onValueChange={(v) => setField('size', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Selecciona talla" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sin talla —</SelectItem>
                  {SIZES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Color</Label>
              <Input
                value={form.color || ''}
                onChange={(e) => setField('color', e.target.value)}
                placeholder="ej. Azul marino"
              />
            </div>
          </div>

          {/* Modificaciones */}
          <div className="space-y-1">
            <Label>Modificaciones</Label>
            <Textarea value={form.changes_requested} onChange={(e) => setField('changes_requested', e.target.value)} placeholder="Describe las modificaciones solicitadas..." />
          </div>

          {/* Measurements */}
          <div className="space-y-2">
            <div className="hidden md:grid md:grid-cols-12 gap-3 pb-1 border-b text-xs font-medium text-slate-500 uppercase tracking-wide">
              <div className="col-span-3">Medida</div>
              <div className="col-span-2">Valor</div>
              <div className="col-span-7">Nota</div>
            </div>

            {MEAS_KEYS.map((k) => (
              <div
                key={k}
                className={`grid grid-cols-12 gap-3 items-center rounded-lg px-3 py-2 transition-colors ${
                  measEnabled[k] ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-slate-200'
                }`}
              >
                <div className="col-span-3 flex items-center gap-2">
                  <Checkbox
                    id={`meas-${k}`}
                    checked={measEnabled[k]}
                    onCheckedChange={() => toggleMeas(k)}
                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <Label
                    htmlFor={`meas-${k}`}
                    className={`capitalize cursor-pointer select-none font-medium text-sm ${
                      measEnabled[k] ? 'text-blue-700' : 'text-slate-500'
                    }`}
                  >
                    {k}
                  </Label>
                </div>
                <div className="col-span-2">
                  <Input
                    value={form.measurements[k]}
                    onChange={(e) => setMeas(k, e.target.value)}
                    placeholder='ej. 36"'
                    disabled={!measEnabled[k]}
                    className={`text-sm ${!measEnabled[k] ? 'opacity-40 bg-slate-100' : 'bg-white'}`}
                  />
                </div>
                <div className="col-span-7">
                  <Input
                    value={measNotes[k]}
                    onChange={(e) => setMeasNotes((n) => ({ ...n, [k]: e.target.value }))}
                    placeholder={measEnabled[k] ? 'Agregar nota...' : '—'}
                    disabled={!measEnabled[k]}
                    className={`text-sm ${!measEnabled[k] ? 'opacity-40 bg-slate-100' : 'bg-white'}`}
                  />
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-400 pt-1">Activa solo las medidas que requieren ajuste.</p>
          </div>

          <div className="space-y-1">
            <Label>Notas (opcional)</Label>
            <Textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} placeholder="Observaciones..." />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Servicios adicionales</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2 space-y-1">
              <Label>Servicio</Label>
              <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
                <SelectTrigger><SelectValue placeholder="Selecciona servicio" /></SelectTrigger>
                <SelectContent>
                  {serviceProducts.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} - {money(s.price)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Cantidad</Label>
              <Input value={serviceQty} onChange={(e) => setServiceQty(e.target.value)} placeholder="1" />
            </div>
            <div className="flex items-end">
              <Button type="button" variant="outline" onClick={addService}>Agregar servicio</Button>
            </div>
          </div>

          <div className="space-y-3">
            {(!form.services || form.services.length === 0) ? (
              <p className="text-sm text-center text-slate-400 py-4">No hay servicios adicionales.</p>
            ) : (
              (form.services || []).map((s) => (
                <div key={s.product_id} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50">
                  {/* Header row: nombre, qty, precio, subtotal, quitar */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="font-medium text-sm truncate">{s.name}</span>
                      <span className="text-xs text-slate-500 whitespace-nowrap">x{s.quantity} · {money(s.unit_price)}</span>
                      <span className="text-sm font-semibold text-cyan-600 whitespace-nowrap">{money(s.subtotal)}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeService(s.product_id)}
                      className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 shrink-0"
                    >
                      Quitar
                    </Button>
                  </div>
                  {/* Personalización */}
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500 whitespace-nowrap">Personalización:</Label>
                    <Input
                      value={s.customization || ''}
                      onChange={(e) => updateServiceCustomization(s.product_id, e.target.value)}
                      placeholder="Describe la personalización de este servicio..."
                      className="text-sm h-8"
                    />
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="text-sm text-slate-600">Total servicios: <span className="font-medium">{money(servicesTotal)}</span></div>
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button onClick={submit} disabled={saving}>Guardar</Button>
        <Button variant="outline" onClick={() => navigate('/tailoring-orders')} disabled={saving}>Cancelar</Button>
      </div>

      {/* Modal actualizar perfil del cliente */}
      <Dialog open={!!profileModal} onOpenChange={() => handleProfileUpdate(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Actualizar perfil del cliente?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Se detectaron diferencias en <strong>{profileModal?.customerName}</strong> respecto al pedido actual:
            </p>
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {(profileModal?.changes || []).map((ch, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2">
                  <span className="font-medium text-slate-700 w-32 shrink-0">{ch.label}:</span>
                  <span className="text-red-400 line-through text-xs">{ch.from}</span>
                  <span className="text-slate-400 mx-1">→</span>
                  <span className="text-green-600 font-medium">{ch.to}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">Esto actualizará el perfil del cliente para futuros pedidos.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => handleProfileUpdate(false)} disabled={updatingProfile}>
              No, solo guardar pedido
            </Button>
            <Button onClick={() => handleProfileUpdate(true)} disabled={updatingProfile}
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {updatingProfile ? 'Actualizando...' : 'Actualizar perfil'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal nuevo cliente */}
      {newCustModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Nuevo Cliente
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Nombre *</Label>
                <Input
                  value={newCust.name}
                  onChange={(e) => setNewCust((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Juan"
                  autoFocus
                />
              </div>
              <div className="space-y-1">
                <Label>Apellido *</Label>
                <Input
                  value={newCust.last_name}
                  onChange={(e) => setNewCust((p) => ({ ...p, last_name: e.target.value }))}
                  placeholder="Pérez"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Teléfono *</Label>
                <Input
                  value={newCust.phone}
                  onChange={(e) => setNewCust((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="809-000-0000"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateCustomer()}
                />
              </div>
            </div>

            <p className="text-xs text-slate-400">* Campos requeridos. Puedes agregar más datos desde el módulo de clientes.</p>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setNewCustModal(false); setNewCust({ name: '', last_name: '', phone: '' }); }}>
                Cancelar
              </Button>
              <Button onClick={handleCreateCustomer} disabled={savingCust} className="bg-blue-600 hover:bg-blue-700 text-white">
                <UserPlus className="w-4 h-4 mr-2" />
                {savingCust ? 'Guardando...' : 'Crear cliente'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TailoringOrderNew;